import React, { useEffect, useMemo } from 'react'
import {
  ButtonGroup,
  ButtonGroupItem,
} from 'design-system/ui/molecules/button_group/index'
import { SearchableDropdown } from 'design-system/ui/molecules/dropdown-search'
import { useAtom } from 'jotai'
import { ChevronDown } from 'lucide-react'

import type { REQUEST_LOGS_STATUS_VALUES } from '~/constants/analytics'
import { ACTION_LOGS_LABELS } from '~/constants/actions'
import { QUERY_PARAMS } from '~/constants/analytics'
import { UpdateQueryAtom } from '~/hooks/utils/queryParams'

export const StatusDot = ({
  status,
  className,
}: {
  status: 'all' | 'success' | 'failed'
  className?: string
}) => {
  const statusColor =
    status === 'success'
      ? 'ds-bg-green-400'
      : status === 'failed'
        ? 'ds-bg-red-400'
        : 'ds-bg-blue-400'
  const borderColor =
    status === 'success'
      ? 'ds-border-green-800'
      : status === 'failed'
        ? 'ds-border-red-800'
        : 'ds-border-blue-800'
  return (
    <div
      className={`h-[10px] w-[10px] rounded-full ${statusColor} border-[1px] ${borderColor} ${className}`}
    />
  )
}

interface StatusSelectorProps {
  onChange: (value: string) => void
}

export const StatusSelector = ({ onChange }: StatusSelectorProps) => {
  const [queryParams, updateQueryParams] = useAtom(UpdateQueryAtom)

  const statusValue = (queryParams[QUERY_PARAMS.REQUEST_LOGS.STATUS.key] ||
    QUERY_PARAMS.REQUEST_LOGS.STATUS.defaultValue) as REQUEST_LOGS_STATUS_VALUES

  const availableStatusValues = QUERY_PARAMS.REQUEST_LOGS.STATUS.availableValues
  const label = useMemo(() => {
    return ACTION_LOGS_LABELS.STATUS[statusValue]
  }, [statusValue])

  // @Note: Dangerous, but it works because it's string. Make these all components
  // stateless.
  useEffect(() => {
    onChange(statusValue)
  }, [statusValue])

  return (
    <SearchableDropdown
      selectedValue={statusValue}
      dataItems={[
        {
          children: [
            {
              component_type: 'menu_item',
              label: 'All',
              value: availableStatusValues[0],
              leftIcon: <StatusDot status="all" className="ds-mr-4" />,
            },
            {
              component_type: 'menu_item',
              label: 'Success',
              value: availableStatusValues[1],
              leftIcon: <StatusDot status="success" className="ds-mr-4" />,
            },
            {
              component_type: 'menu_item',
              label: 'Failed',
              value: availableStatusValues[2],
              leftIcon: <StatusDot status="failed" className="ds-mr-4" />,
            },
          ],
          component_type: 'menu_item_group',
        },
      ]}
      onChange={(value: string) => {
        updateQueryParams({
          operation: 'update',
          data: {
            [QUERY_PARAMS.REQUEST_LOGS.STATUS.key]: value,
          },
        })
        onChange(value)
      }}
      dialogClassName="min-w-[132px]"
    >
      <ButtonGroup size="32">
        <React.Fragment>
          <ButtonGroupItem className="!ds-bg-white-50">Showing</ButtonGroupItem>

          <ButtonGroupItem showRightBorder={false}>
            {label} <ChevronDown height={12} width={12} strokeWidth={1.5} />
          </ButtonGroupItem>
        </React.Fragment>
      </ButtonGroup>
    </SearchableDropdown>
  )
}
